import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Save, ArrowLeft, Car, Building, Heart, Wifi, BookOpen, Settings, MapPin, Users } from 'lucide-react';
import { useContent, Facility } from '../../contexts/ContentContext';

const FacilityEditor: React.FC = () => {
  const navigate = useNavigate();
  const { facilityId } = useParams<{ facilityId?: string }>();
  const { facilities, addFacility, updateFacility } = useContent();
  const isEditing = facilityId && facilityId !== 'new';

  const [facility, setFacility] = useState<Omit<Facility, 'id'> & { id?: number }>({
    name: '',
    description: '',
    icon: 'Settings',
    category: 'Umum',
    isActive: true
  });

  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (isEditing) {
      const existingFacility = facilities.find(f => f.id === parseInt(facilityId!));
      if (existingFacility) {
        setFacility(existingFacility);
      }
    }
  }, [isEditing, facilityId, facilities]);

  const handleSave = async () => {
    if (!facility.name.trim() || !facility.description.trim()) {
      alert('Mohon lengkapi semua field yang wajib diisi');
      return;
    }

    setIsLoading(true);
    try {
      if (isEditing && facility.id) {
        await updateFacility(facility.id, facility);
        alert('Fasilitas berhasil diperbarui');
      } else {
        await addFacility({
          name: facility.name,
          description: facility.description,
          icon: facility.icon,
          category: facility.category,
          isActive: facility.isActive ?? true
        });
        alert('Fasilitas berhasil ditambahkan');
      }
      
      // Navigate back to content management
      navigate('/admin/content-management');
    } catch (error) {
      alert('Terjadi kesalahan saat menyimpan data');
    } finally {
      setIsLoading(false);
    }
  };

  // Icon mapping to match the landing page icons
  const iconOptions = [
    { value: 'Car', label: '🚗 Transportasi Darat', component: Car },
    { value: 'MapPin', label: '📍 Bandara/Lokasi', component: MapPin },
    { value: 'Building', label: '🏢 Industri/Gedung', component: Building },
    { value: 'BookOpen', label: '📚 Pendidikan', component: BookOpen },
    { value: 'Heart', label: '❤️ Kesehatan', component: Heart },
    { value: 'Settings', label: '⚙️ Energi/Utilitas', component: Settings },
    { value: 'Wifi', label: '📶 Telekomunikasi', component: Wifi },
    { value: 'Users', label: '👥 SDM/Tenaga Kerja', component: Users }
  ];

  const categoryOptions = [
    'Transportasi',
    'Industri', 
    'Pendidikan',
    'Kesehatan',
    'Energi',
    'Telekomunikasi',
    'SDM',
    'Umum'
  ];

  const selectedIcon = iconOptions.find(option => option.value === facility.icon);
  const IconComponent = selectedIcon?.component || Settings;

  return (
    <div style={{ padding: '20px', maxWidth: '800px', margin: '0 auto' }}>
      <div style={{ marginBottom: '30px', borderBottom: '2px solid #e5e7eb', paddingBottom: '20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
          <button 
            onClick={() => navigate('/admin/content-management')}
            style={{ 
              background: 'none', 
              border: '1px solid #d1d5db', 
              color: '#374151', 
              padding: '8px 12px', 
              borderRadius: '6px', 
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}
          >
            <ArrowLeft className="w-4 h-4" />
            Kembali
          </button>
        </div>
        <h1 style={{ margin: '0', color: '#111827', fontSize: '24px' }}>
          {isEditing ? 'Edit Fasilitas Pendukung' : 'Tambah Fasilitas Pendukung'}
        </h1>
        <p style={{ margin: '8px 0 0', color: '#6b7280' }}>
          {isEditing ? 'Perbarui informasi fasilitas pendukung' : 'Tambahkan fasilitas pendukung baru'}
        </p>
      </div>

      <div style={{ background: '#f9fafb', padding: '30px', borderRadius: '12px', border: '1px solid #e5e7eb' }}>
        <div style={{ display: 'grid', gap: '20px', gridTemplateColumns: '1fr 1fr', marginBottom: '30px' }}>
          {/* Left Column - Form */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#374151' }}>
                Nama Fasilitas *
              </label>
              <input
                type="text"
                value={facility.name}
                onChange={(e) => setFacility({ ...facility, name: e.target.value })}
                placeholder="Masukkan nama fasilitas"
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '14px',
                  outline: 'none',
                  transition: 'border-color 0.3s ease'
                }}
                onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#374151' }}>
                Kategori
              </label>
              <select
                value={facility.category}
                onChange={(e) => setFacility({ ...facility, category: e.target.value })}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '14px',
                  outline: 'none',
                  background: 'white'
                }}
                onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
              >
                {categoryOptions.map(category => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#374151' }}>
                Icon (harus sesuai dengan landing page)
              </label>
              <select
                value={facility.icon}
                onChange={(e) => setFacility({ ...facility, icon: e.target.value })}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '14px',
                  outline: 'none',
                  background: 'white'
                }}
                onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
              >
                {iconOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#374151' }}>
                Deskripsi *
              </label>
              <textarea
                value={facility.description}
                onChange={(e) => setFacility({ ...facility, description: e.target.value })}
                placeholder="Masukkan deskripsi fasilitas"
                rows={4}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '14px',
                  outline: 'none',
                  resize: 'vertical',
                  minHeight: '100px'
                }}
                onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
              />
            </div>
          </div>

          {/* Right Column - Preview */}
          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#374151' }}>
              Preview (seperti di landing page)
            </label>
            <div style={{ 
              background: 'white', 
              border: '1px solid #e5e7eb', 
              borderRadius: '12px', 
              padding: '24px',
              boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
              textAlign: 'center'
            }}>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                width: '80px', 
                height: '80px', 
                margin: '0 auto 16px', 
                background: '#1e3a8a', 
                borderRadius: '50%', 
                color: 'white'
              }}>
                <IconComponent className="w-8 h-8" />
              </div>
              <h3 style={{ margin: '0 0 12px 0', fontSize: '18px', fontWeight: '600', color: '#1f2937' }}>
                {facility.name || 'Nama Fasilitas'}
              </h3>
              <p style={{ 
                margin: '0 0 16px 0', 
                fontSize: '14px', 
                color: '#6b7280', 
                lineHeight: '1.5'
              }}>
                {facility.description || 'Deskripsi fasilitas akan muncul di sini'}
              </p>
              <span style={{ 
                display: 'inline-block', 
                padding: '6px 12px', 
                background: '#f3f4f6', 
                color: '#374151',
                borderRadius: '999px', 
                fontSize: '12px',
                fontWeight: '500'
              }}>
                {facility.category}
              </span>
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', borderTop: '1px solid #e5e7eb', paddingTop: '20px' }}>
          <button
            onClick={() => navigate('/admin/content-management')}
            disabled={isLoading}
            style={{
              background: 'white',
              color: '#374151',
              border: '1px solid #d1d5db',
              padding: '12px 20px',
              borderRadius: '8px',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              opacity: isLoading ? 0.5 : 1
            }}
          >
            Batal
          </button>
          <button
            onClick={handleSave}
            disabled={isLoading}
            style={{
              background: '#3b82f6',
              color: 'white',
              border: 'none',
              padding: '12px 20px',
              borderRadius: '8px',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              opacity: isLoading ? 0.5 : 1
            }}
          >
            <Save className="w-4 h-4" />
            {isLoading ? 'Menyimpan...' : 'Simpan'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FacilityEditor;